import { cloneSerializableFieldMap } from '@/common/utils/cloneSerializableFieldMap'
import {
  mergePageFieldMaps,
} from '@/common/utils/mergePageFieldMaps'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import type { AppStore } from '@/stores/app'

/** Снимок полей слота pagesData перед уходом на дочернее окно (NEW_WINDOW и т.п.). */
export const PAGE_SLOT_FIELD_SNAPSHOT_KEY = '$pageFieldSnapshot'

export function readPageSlotFieldSnapshot(
  pageData: Record<string, unknown> | null | undefined,
): Record<string, unknown> | undefined {
  if (!pageData || typeof pageData !== 'object') return undefined
  const snap = pageData[PAGE_SLOT_FIELD_SNAPSHOT_KEY]
  if (!snap || typeof snap !== 'object' || Array.isArray(snap)) return undefined
  return snap as Record<string, unknown>
}

export function hasPageSlotFieldSnapshot(
  pageData: Record<string, unknown> | null | undefined,
): boolean {
  const snap = readPageSlotFieldSnapshot(pageData)
  return Boolean(snap && Object.keys(snap).length > 0)
}

function applySnapshotToFieldMap(
  existing: Record<string, any>,
  snap: Record<string, unknown>,
): Record<string, any> {
  const preserve = { shouldPreserveEmptyIncoming: () => true }
  // При restore снимок только дополняет: не перезаписывает более свежие значения
  // уже лежащие в store (например, родитель обновлён merge при закрытии диалога).
  return mergePageFieldMaps(preserve, snap, existing)
}

function buildRestoredPagePayload(
  pd: Record<string, unknown>,
  snap: Record<string, unknown>,
): Record<string, unknown> {
  const uniFields = applySnapshotToFieldMap(
    (pd.uniData as any)?.fields?.fields || {},
    snap,
  )
  const changedFields = applySnapshotToFieldMap(
    (pd.jsResult as any)?.changed_data?.fields?.fields || {},
    snap,
  )

  const chainRoot = pd.currentChainData as Record<string, unknown> | undefined
  const chainFieldsRaw = (chainRoot?.fields as Record<string, unknown> | undefined)?.fields

  const nextPayload: Record<string, unknown> = {
    ...pd,
    uniData: {
      ...(pd.uniData as object || {}),
      fields: {
        ...((pd.uniData as any)?.fields || {}),
        fields: uniFields,
      },
    },
    jsResult: {
      ...(pd.jsResult as object || {}),
      changed_data: {
        ...((pd.jsResult as any)?.changed_data || {}),
        fields: {
          ...((pd.jsResult as any)?.changed_data?.fields || {}),
          fields: changedFields,
        },
      },
    },
  }

  if (chainFieldsRaw && typeof chainFieldsRaw === 'object' && !Array.isArray(chainFieldsRaw)) {
    nextPayload.currentChainData = {
      ...(chainRoot || {}),
      fields: {
        ...(chainRoot?.fields as object || {}),
        fields: applySnapshotToFieldMap(chainFieldsRaw as Record<string, any>, snap),
      },
    }
  }

  return nextPayload
}

/** Сохранить снимок полей слота (перед открытием дочернего окна). */
export function capturePageSlotFieldSnapshot(appStore: AppStore, slotId: string): void {
  const id = String(slotId ?? '').trim()
  if (!id) return

  const pd = appStore.pagesData[id] as Record<string, unknown> | undefined
  if (!pd || typeof pd !== 'object') return

  const fields = mergePageDataFieldSnapshot(pd)
  if (!fields || Object.keys(fields).length === 0) return

  try {
    pd[PAGE_SLOT_FIELD_SNAPSHOT_KEY] = cloneSerializableFieldMap(fields)
  } catch {
    /* снимок опционален */
  }
}

/** Перед NEW_WINDOW: снимок родительского слота, чтобы при возврате не терять таблицы. */
export function capturePageSlotSnapshotBeforeChildWindow(
  appStore: AppStore | undefined,
  parentPageSlotId: string | null | undefined,
  windowType: string | undefined,
): void {
  if (String(windowType ?? '').toUpperCase() !== 'NEW_WINDOW') return
  const parent = String(parentPageSlotId ?? '').trim()
  if (!parent || !appStore) return
  capturePageSlotFieldSnapshot(appStore, parent)
}

/** Восстановить uniData/changed_data/chain из снимка; true если снимок применён. */
export function restorePageSlotFieldSnapshot(appStore: AppStore, slotId: string): boolean {
  const id = String(slotId ?? '').trim()
  if (!id) return false

  const pd = appStore.pagesData[id] as Record<string, unknown> | undefined
  if (!pd || typeof pd !== 'object') return false

  const snap = readPageSlotFieldSnapshot(pd)
  if (!snap || Object.keys(snap).length === 0) return false

  appStore.setPageData(
    id,
    buildRestoredPagePayload(pd, snap),
    undefined,
    undefined,
    'restorePageSlotFieldSnapshot',
  )
  return true
}
